'use client';

import { useState } from 'react';
import Image from 'next/image';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { authClient } from '@/lib/auth/client';

const ease = [0.22, 1, 0.36, 1] as const;

export function ResetPasswordForm() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await authClient.requestPasswordReset?.({
        email,
        redirectTo: '/reset-password',
      });
    } catch {
      // Intentionally swallowed — always show confirmation for security
    } finally {
      setSubmittedEmail(email);
      setSubmitted(true);
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
      className="w-full"
    >
      {/* Mobile logo */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5, ease }}
        className="lg:hidden mb-10"
      >
        <Image
          src="https://assets.getcreatr.com/uploads/1778677644595-4d8s0hnts6u-image.png"
          alt="Everspark"
          width={130}
          height={30}
          className="h-7 w-auto"
          priority
        />
      </motion.div>

      {!submitted ? (
        <>
          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6, ease }}
            className="mb-8"
          >
            <h2 className="font-display italic text-[2rem] lg:text-[2.25rem] text-foreground leading-tight">
              Reset your password
            </h2>
            <p className="mt-1.5 text-muted-foreground text-[15px]">
              Enter your email and we&apos;ll send you a reset link
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6, ease }}
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label
                htmlFor="reset-email"
                className="text-xs font-sans font-medium tracking-wider uppercase text-muted-foreground"
              >
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-muted-foreground/50 pointer-events-none" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@everspark.com"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 pl-10 rounded-lg bg-card text-sm placeholder:text-muted-foreground/40"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 rounded-lg text-sm font-sans font-medium tracking-wide"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Sending reset link...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </motion.form>

          {/* Back to sign in */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-6 text-center"
          >
            <a
              href="/login"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to sign in
            </a>
          </motion.div>
        </>
      ) : (
        /* Confirmation state */
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease }}
          className="space-y-6"
        >
          <div className="flex flex-col gap-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
              <CheckCircle2 className="h-6 w-6 text-success" />
            </div>
            <div>
              <h2 className="font-display italic text-[2rem] text-foreground leading-tight">
                Check your email
              </h2>
              <p className="mt-2 text-[15px] text-muted-foreground leading-relaxed">
                We sent a reset link to{' '}
                <span className="font-medium text-foreground">
                  {submittedEmail}
                </span>
                . The link expires in 1 hour.
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-border/60 bg-muted/40 px-4 py-3">
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              <AlertCircle className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" />
              Didn&apos;t receive an email? Check your spam folder, or{' '}
              <button
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }}
                className="text-foreground underline-offset-2 hover:underline transition-colors"
              >
                try again
              </button>
              .
            </p>
          </div>

          <a
            href="/login"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </a>
        </motion.div>
      )}
    </motion.div>
  );
}
